// 🔧 ticket_repository.js
// ✅ Purpose: Firestore repository for Ticket Engine demo data
// ✅ Features:
// - Writes production ticket type documents to the tickets collection
// - Writes completed demo sales to the orders collection
// - Uses Firestore-generated document IDs
// - Uses production collection paths
// - No business logic
// - Fully block-commented for clarity and future devs

import {
  collection,
  doc,
  writeBatch,
  serverTimestamp,
} from "firebase/firestore";

import { db } from "../../../firebase";

export const saveTicketData = async ({
  demoContext,
  ticketTypes = [],
  tickets = [],
} = {}) => {
  console.log("💾 Saving Ticket Engine demo data.");

  const eventId =
    demoContext?.eventId ||
    demoContext?.eventResult?.eventId ||
    demoContext?.event?.eventId;

  if (!eventId) {
    throw new Error("Ticket Repository requires an eventId.");
  }

  if (ticketTypes.length === 0) {
    return {
      ticketTypes: [],
      tickets: [],
    };
  }

  const batch = writeBatch(db);

  const savedTicketTypes = [];
  const savedOrders = [];
  const ticketTypeIdByLocalKey = {};
  const quantitySoldByLocalKey = tickets.reduce(
    (totals, ticket) => ({
      ...totals,
      [ticket.ticketTypeLocalKey]:
        Number(totals[ticket.ticketTypeLocalKey] || 0) +
        Number(ticket.quantity || 1),
    }),
    {}
  );

  ticketTypes.slice(0, 3).forEach((ticketType) => {
    const ticketTypeRef = doc(
      collection(db, "events", eventId, "tickets")
    );

    const ticketTypeData = {
      ...ticketType,
      title: ticketType.title || ticketType.name || "Event Ticket",
      eventId,
      quantitySold: Number(quantitySoldByLocalKey[ticketType.localKey] || 0),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      demoSeed: true,
    };

    batch.set(ticketTypeRef, ticketTypeData);

    ticketTypeIdByLocalKey[ticketType.localKey] = ticketTypeRef.id;

    savedTicketTypes.push({
      ...ticketTypeData,
      id: ticketTypeRef.id,
    });
  });

  tickets.forEach((ticket) => {
    const ticketId =
      ticketTypeIdByLocalKey[ticket.ticketTypeLocalKey] || "";

    if (!ticketId) return;

    const quantity = Number(ticket.quantity || 1);
    const unitAmount = Number(ticket.amountPaidCents || 0);
    const orderRef = doc(
      collection(db, "events", eventId, "orders")
    );

    const orderData = {
      orderType: "ticket",
      provider: "demo",
      status: "paid",
      eventId,
      ticketId,
      ticketTitle: ticket.ticketTypeName || "Event Ticket",
      buyerName: ticket.purchaserName || "Demo Attendee",
      buyerEmail: ticket.purchaserEmail || "",
      quantity,
      unitAmount,
      totalAmount: unitAmount * quantity,
      amountCents: unitAmount * quantity,
      currency: "usd",
      confirmationEmailSent: true,
      checkInStatus: ticket.checkInStatus || "not_checked_in",
      ticketCode: ticket.ticketCode || "",
      paidAt: serverTimestamp(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      demoSeed: true,
    };

    batch.set(orderRef, orderData);

    savedOrders.push({
      ...orderData,
      id: orderRef.id,
    });
  });

  await batch.commit();

  console.log("✅ Ticket Engine repository completed.", {
    ticketTypes: savedTicketTypes.length,
    orders: savedOrders.length,
  });

  return {
    ticketTypes: savedTicketTypes,
    // Compatibility alias: reports historically call completed sales tickets.
    tickets: savedOrders,
    orders: savedOrders,
  };
};
